/* eslint-disable react/react-in-jsx-scope */
import ReactBreadcrumb from "@/components/breadcrumb/Breadcrumb";
import { toast } from "@/components/ui/use-toast";
import { Pencil, Settings, Check, Copy, Unlock, Lock, Eye , NotebookPen} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { User } from "@/types";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AddSubcontractorDialog from "@/dashboard/components/subcontractor/AddSubcontractorDialog";
import EmailDialog from "@/dashboard/components/subcontractor/ShareEmailDialog";
import {
  DASHBOARD_QUERY_GC_TIME,
  DASHBOARD_QUERY_STALE_TIMES,
  dashboardQueryKeys,
} from "@/dashboard/dashboardQueryConfig";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostData from "@/hooks/usePostData";
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/hooks/useAuth";

const isUser = (obj: any): obj is User => {
  return (
    obj !== null &&
    typeof obj === "object" &&
    typeof obj.id === "number" &&
    typeof obj.email === "string" &&
    typeof obj.firstname === "string" &&
    typeof obj.lastname === "string" &&
    (obj.structureName === undefined || typeof obj.structureName === "string") &&
    (obj.roles === undefined ||
      (Array.isArray(obj.roles) && obj.roles.every((r) => typeof r === "string"))) &&
    (obj.isVerified === undefined || typeof obj.isVerified === "boolean") &&
    (obj.status === undefined || typeof obj.status === "number") &&
    (obj.codeMeta === undefined || typeof obj.codeMeta === "string")
  );
};

const TableSkeleton = ({ columns = 4, rows = 3 }) => {
  return (
    <Table className="w-full border-collapse">
      <TableHeader>
        <TableRow>
          {[...Array(columns)].map((_, colIndex) => (
            <TableHead key={colIndex}>
              <Skeleton height={20} width="80%" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(rows)].map((_, rowIndex) => (
          <TableRow key={rowIndex}>
            {[...Array(columns)].map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton height={18} width="90%" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

type Coworker = {
  id: string | number;
  nom: string;
  prenom: string;
  mail: string;
  status: number | null;
  access: boolean; // true => Lecture / Ecriture ; false => Lecture
};

const normalizeCoworker = (coworker: any): Coworker => ({
  id: coworker?.id,
  nom: coworker?.nom ?? coworker?.lastname ?? "",
  prenom: coworker?.prenom ?? coworker?.firstname ?? "",
  mail: coworker?.mail ?? coworker?.email ?? "",
  status:
    typeof coworker?.status === "number"
      ? coworker.status
      : coworker?.status ?? null,
  access:
    typeof coworker?.access === "boolean"
      ? coworker.access
      : Boolean(coworker?.fullAccess),
});

const STATIC_COWORKERS: Coworker[] = [
  {
    id: "101",
    nom: "Rasoanaivo",
    prenom: "Mika",
    mail: "mika.rasoanaivo@exemple.com",
    status: 1,
    access: true,
  },
  {
    id: "102",
    nom: "Rakoto",
    prenom: "Aina",
    mail: "aina.rakoto@exemple.com",
    status: 1,
    access: false,
  },
  {
    id: "103",
    nom: "Randria",
    prenom: "Soa",
    mail: "soa.randria@exemple.com",
    status: 0,
    access: true,
  },
];

const UserManagement = () => {
  const queryClient = useQueryClient();
  const { postData } = usePostData();
  const [isLoading, setIsloading] = useState(true);
  const coworkersEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/users/coworkers`;
  const coworkersQueryKey = [dashboardQueryKeys.coworkers, coworkersEndpoint];

  const coworkers = useFetchDataV2(
    coworkersEndpoint,
    {
      browserCache: true,
      browserCacheKey: "coworkers",
      browserCacheNamespace: "dashboard",
      browserCacheTtlMs: 1000 * 60 * 60,
      cacheId: dashboardQueryKeys.coworkers,
      gcTime: DASHBOARD_QUERY_GC_TIME,
      staleTime: DASHBOARD_QUERY_STALE_TIMES.coworkers,
    },
  );

  const [data, setData] = useState<Coworker[]>([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const [currentItem, setCurrentItem] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const navigate = useNavigate();
  const { user, updateUserState } = useAuth();
  const [codeMeta, setCodeMeta] = useState("");
  const [isUserLoaded, setIsUserLoaded] = useState(false);

  useEffect(() => {
    if (user) setIsUserLoaded(true);
    if (user?.codeMeta) setCodeMeta(user?.codeMeta);
  }, [user]);

  const [copied, setCopied] = useState(false);
  const [isLoadingGenerate, setIsLoadingGenerate] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(
        window.location.origin + "/submit/user-partner-subscription/" + codeMeta
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Erreur lors de la copie", err);
    }
  };

  const generateUniqueCode = async () => {
    setIsLoadingGenerate(true);
    postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/user/generate-metacode/" + user.id,
      data: {},
    }).then((res: any) => {
      if (isUser(res)) {
        updateUserState(res);
        toast({
          title: "Nouveau lien généré avec succès.",
        });
      }
      setIsLoadingGenerate(false);
    });
  };

  // Données statiques pour aperçu
  // useEffect(() => {
  //   setData(STATIC_COWORKERS);
  //   setIsloading(false);
  // }, []);

  
  useEffect(() => {
    if (coworkers?.data !== undefined) {
      setData(
        coworkers?.data?.length
          ? coworkers?.data?.map(normalizeCoworker)
          : ([] as Coworker[])
      );
      setIsloading(false);
    }
  }, [coworkers?.data]);

  const updateCoworkersCache = (
    updater: (current: Coworker[] | undefined) => Coworker[] | undefined,
  ) => {
    queryClient.setQueriesData(
      {
        exact: false,
        queryKey: [dashboardQueryKeys.coworkers],
      },
      (current: any) => {
        const normalized = Array.isArray(current)
          ? current.map(normalizeCoworker)
          : undefined;
        return updater(normalized);
      },
    );
  };

  const updateCoworkers = (
    updater: Coworker[] | ((current: Coworker[]) => Coworker[]),
  ) => {
    setData((current) => {
      const next =
        typeof updater === "function"
          ? (updater as (current: Coworker[]) => Coworker[])(current)
          : updater;
      queryClient.setQueryData(coworkersQueryKey, next);
      updateCoworkersCache(() => next);
      return next;
    });
  };

  const snapshotCoworkersQueries = () =>
    queryClient.getQueriesData<Coworker[]>({
      exact: false,
      queryKey: [dashboardQueryKeys.coworkers],
    });

  const restoreCoworkersQueries = (
    snapshots: [unknown, Coworker[] | undefined][],
  ) => {
    snapshots.forEach(([queryKey, previousData]) => {
      queryClient.setQueryData(queryKey as any, previousData);
    });
  };

  const applyOptimisticCoworkerSave = (draftCoworker: any) => {
    const snapshots = snapshotCoworkersQueries();
    const normalizedDraft = normalizeCoworker(draftCoworker);

    updateCoworkers((current) => {
      const exists = current.some(
        (coworker) => String(coworker.id) === String(normalizedDraft.id),
      );
      return exists
        ? current.map((coworker) =>
            String(coworker.id) === String(normalizedDraft.id)
              ? { ...coworker, ...normalizedDraft }
              : coworker,
          )
        : [normalizedDraft, ...current];
    });

    return { snapshots, optimisticId: normalizedDraft.id };
  };

  const upsertCoworker = (coworker: any, optimisticId?: string | number) => {
    const normalizedCoworker = normalizeCoworker(coworker);
    if (!normalizedCoworker.id) {
      return;
    }

    updateCoworkers((current) => {
      const exists = current.some(
        (item) =>
          String(item.id) === String(normalizedCoworker.id) ||
          String(item.id) === String(optimisticId),
      );
      return exists
        ? current.map((item) =>
            String(item.id) === String(normalizedCoworker.id) ||
            String(item.id) === String(optimisticId)
              ? { ...item, ...normalizedCoworker }
              : item,
          )
        : [normalizedCoworker, ...current];
    });
  };

  const rollbackCoworkers = (meta: any) => {
    if (!Array.isArray(meta?.snapshots)) return;
    restoreCoworkersQueries(meta.snapshots);
    const restored = queryClient.getQueryData<Coworker[]>(coworkersQueryKey);
    setData(Array.isArray(restored) ? restored.map(normalizeCoworker) : []);
  };

  const normalizeCoworkerResponse = (response: any) =>
    response?.user ?? response?.coworker ?? response?.data ?? response;

  const displayedData = useMemo(
    () =>
      data.length > 0
        ? data
        : Array.isArray(coworkers?.data)
          ? coworkers.data.map(normalizeCoworker)
          : [],
    [coworkers?.data, data],
  );

  const displayLoading =
    isLoading && displayedData.length === 0 && coworkers.loading;

  useEffect(() => {
    if (!openAddModal) setIsEditMode(false);
  }, [openAddModal]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const columns: ColumnDef<Coworker>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "nom",
      header: "Nom",
      cell: ({ row }) => <div>{row.getValue("nom")}</div>,
    },
    {
      accessorKey: "prenom",
      header: "Prénom",
      cell: ({ row }) => <div>{row.getValue("prenom")}</div>,
    },
    {
      accessorKey: "mail",
      header: "Email",
      cell: ({ row }) => <div>{row.getValue("mail")}</div>,
    },
    {
      accessorKey: "access",
      header: "Droit",
      cell: ({ row }) => {
        const access = row.getValue("access") as boolean;

        return (
          <div
            className={`flex items-center gap-2 ${
              access === true ? "text-green-600" : "text-orange-600"
            }`}
          >
            {access === true ? <NotebookPen className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {access === true ? "Lecture / Ecriture" : "Lecture"}
          </div>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setCurrentItem(row.original);
              setIsEditMode(true);
              setOpenAddModal(true);
            }}
            className="text-gray-600 hover:text-blue-800"
            title={"Modifier"}
          >
            <Settings size={20} />
          </button>
        </div>
      ),
    },
  ];

  const table = useReactTable({
    data: displayedData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleDelete = async () => {
    setLoadingDelete(true);

    const ids = table.getFilteredSelectedRowModel().rows.map((row) => row.original.id);
    const snapshots = snapshotCoworkersQueries();

    updateCoworkers((current) =>
      current.filter(
        (coworker) => !ids.some((id) => String(id) === String(coworker.id)),
      ),
    );
    setRowSelection({});
    setOpenModalDelete(false);

    // ⚠️ API link inchangé
    const res = await postData({
      url: import.meta.env.VITE_API_ENDPOINT + "/users/coworkers/delete",
      data: { ids },
    });

    if (res?.success) {
      toast({
        title: "Les coworkers ont bien été supprimés.",
      });
      setLoadingDelete(false);
      return;
    }

    restoreCoworkersQueries(snapshots);
    const restored = queryClient.getQueryData<Coworker[]>(coworkersQueryKey);
    setData(Array.isArray(restored) ? restored.map(normalizeCoworker) : []);
    setLoadingDelete(false);
  };

  return (
    <div>
      <ReactBreadcrumb
        items={[
          {
            href: ``,
            name: "Gestion des collaborateurs",
          },
        ]}
        isTruncated={false}
      />

      <div className="flex flex-wrap gap-5 items-center justify-between mt-5">
        <h1 className="mb-2 mt-4 text-[24px] font-medium inline-block" id="program-title">
          {"Gestion des collaborateurs"}
        </h1>

        <div className="flex items-center gap-3">
          {!isUserLoaded && (
            <div className="relative flex items-center px-3">
              <Skeleton width={180} height={40} />
            </div>
          )}

          {isUserLoaded && (
            <>
              {codeMeta !== "" ? (
                <>
                  <div className="relative flex items-center px-3">
                    <input
                      type="text"
                      value={window.location.origin + "/submit/user-partner-subscription/" + codeMeta}
                      readOnly
                      className="border border-gray-300 rounded-lg px-3 py-2 bg-gray-100 text-gray-700 cursor-default w-[200px] pr-10"
                    />
                    <button
                      onClick={handleCopy}
                      type="button"
                      className="absolute right-5 text-gray-500 hover:text-gray-700"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>

                  <EmailDialog
                    link={window.location.origin + "/submit/user-partner-subscription/" + codeMeta}
                    isCoworker={true}
                  />
                </>
              ) : (
                <button
                  type="button"
                  className={`px-4 py-2 rounded-lg text-white transition 
                      ${
                        isLoadingGenerate
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-blue-600 cursor-pointer hover:bg-blue-700"
                      }`}
                  disabled={isLoadingGenerate}
                  onClick={() => {
                    generateUniqueCode();
                  }}
                >
                  {isLoadingGenerate ? ".. Génération du lien en cours" : "Générer un nouveau lien"}
                </button>
              )}
            </>
          )}

          {/* ✅ Boutons / composant externe gardé sans changement */}
          <AddSubcontractorDialog
            open={openAddModal}
            onHandleOpen={setOpenAddModal}
            onOptimisticSave={applyOptimisticCoworkerSave}
            onSaveError={rollbackCoworkers}
            onSaveSuccess={(res, meta) => {
              upsertCoworker(
                normalizeCoworkerResponse(res),
                meta?.optimisticId,
              );
              setOpenAddModal(false);
              setIsEditMode(false);
              setCurrentItem(null);
            }}
            subcontractorToEdit={currentItem}
            isEditMode={isEditMode}
            isCoworker={true}
          />
        </div>
      </div>

      <div className="w-full mt-5">
        <div className="rounded-md border">
          {displayLoading ? (
            <TableSkeleton />
          ) : (
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>

              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="h-24 text-center">
                      Pas de résultats.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>

        {displayLoading ? (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              <Skeleton width={180} height={16} />
            </div>
            <div className="space-x-2 flex">
              <Skeleton width={80} height={32} borderRadius={6} />
              <Skeleton width={80} height={32} borderRadius={6} />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end space-x-2 py-4">
            <div className="flex-1 text-sm text-muted-foreground">
              {table.getFilteredSelectedRowModel().rows.length} of{" "}
              {table.getFilteredRowModel().rows.length} ligne(s) sélectionnée(s).
              {table.getFilteredSelectedRowModel().rows.length > 0 && (
                <button
                  className="ml-2 text-primary underline"
                  onClick={() => {
                    setOpenModalDelete(true);
                  }}
                >
                  Supprimer la sélection
                </button>
              )}
            </div>

            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Précédent
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Suivant
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={openModalDelete} onOpenChange={setOpenModalDelete}>
        <DialogContent className="sm:max-h-[80vh] sm:max-w-[50vw] overflow-y-auto">
          <DialogHeader className="">
            <DialogTitle asChild>
              <h2 className="mb-2 mt-5 flex gap-4 text-center text-xl font-bold">
                Supprimer les coworkers
              </h2>
            </DialogTitle>
            <DialogDescription asChild className="text-[#595959]">
              <p className="mt-3">Voulez-vous vraiment supprimer ces coworkers ?</p>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap mt-5 h-full overflow-y-auto">
            <div className="w-full flex justify-end mt-5">
              <DialogFooter className="pb-5">
                <Button onClick={() => handleDelete()} type="button" disabled={loadingDelete}>
                  {loadingDelete ? "... Suppression en cours" : "Supprimer"}
                </Button>
              </DialogFooter>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
